import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useRef } from "react";
import whatInput from "what-input";
import { isFocusable, tabbable } from "tabbable";
import { useUniqueId } from "../hooks/useUniqueId";
import { ScreenReaderContent } from "../screen-reader-content/ScreenReaderContent";
import { useDebounceSync } from "../hooks/useDebounceSync";
import { MotionState } from "./types";
import { useIsAriaDescriptionSupported } from "./useIsAriaDescriptionSupported";
// TODO: Check that this is enough to not break any JSDOM-based tests in packages that import Alchemy via esm or cjs exports
const displayCheck = process.env.NODE_ENV === "test" ? "none" : undefined;
/*
 * Per HTML spec, <dialog> and [popover] elements should support setting the boolean autofocus attribute on the container itself or a nested focusable element,
 * which has higher priority over sequentially focusable (tabbable) elements when choosing a focus delegate for the dialog/popover,
 * see https://html.spec.whatwg.org/multipage/interaction.html#focus-delegate, https://html.spec.whatwg.org/multipage/interactive-elements.html#dialog-focusing-steps
 *
 * React only allows rendering the autoFocus attribute on the server for specific elements like <input> or <select> and strips it from all elements on the client,
 * the workaround for now is to write it like `autofocus="true"` instead of `autoFocus`
 * see https://github.com/facebook/react/blob/493f72b0a7111b601c16b8ad8bc2649d82c184a0/packages/react-dom-bindings/src/client/ReactDOMComponent.js#L638-L644
 */
const getElementToFocus = (overlay) => {
    const autoFocusElement = [...overlay.querySelectorAll("[autofocus], [data-aui-autofocus]")].find((el) => isFocusable(el, { displayCheck }));
    if (autoFocusElement) {
        return autoFocusElement;
    }
    const firstFocusableElement = tabbable(overlay, {
        displayCheck,
        includeContainer: true
    }).find((a) => !("focusBoundary" in a.dataset));
    if (firstFocusableElement) {
        return firstFocusableElement;
    }
    // Fallback to overlay itself, consistent with Dialog and SidePanel's fallbackFocus as well as most recent version of HTML dialog spec as of July 2023
    return overlay;
};
export const usePopperA11y = (config) => {
    const { close, closeAndReturnFocus, isOpen, mode, popoverMode, tooltipMode, openOnDelay, overlayReactElement, overlayRef, portalRoot, shouldCloseOnOutsideInteraction, motionState, triggerReactElement, triggerRef, suppressAriaSupportCheck: __suppressAriaSupportCheck } = config;
    const descriptionSupported = useIsAriaDescriptionSupported(triggerRef);
    const overlayId = useUniqueId("popper-overlay-");
    const overlayHiddenRef = useRef(null);
    const isSilent = mode === "tooltip" && tooltipMode === "silent";
    const isLabel = mode === "tooltip" && tooltipMode === "name";
    const isDescription = mode === "tooltip" && tooltipMode === "description" && descriptionSupported;
    const isLiveRegion = mode === "tooltip" && tooltipMode === "description" && !descriptionSupported;
    const isLiveRegionAnnounced = isLiveRegion && isOpen;
    const isReactControlled = triggerReactElement !== undefined;
    const isLabelledBy = isReactControlled &&
        isLabel &&
        !triggerReactElement?.props["aria-label"] &&
        !triggerReactElement?.props["aria-labelledby"];
    const isDescribedBy = isReactControlled && isDescription && !triggerReactElement?.props["aria-describedby"];
    const triggerProps = {
        ...(isLabelledBy ? { "aria-labelledby": overlayId } : {}),
        ...(isDescribedBy ? { "aria-describedby": overlayId } : {})
    };
    const isLabelRendered = isLabelledBy || (isLabel && !isReactControlled);
    const isDescriptionRendered = isDescribedBy || (isDescription && !isReactControlled);
    /* DES-2482
    useEffect(() => {
      // TODO:
      // - check that trigger contains a tabbable element (for keyboard accessibility)
      // - (for name mode) check that trigger can have an accessible name (for screen reader accessibility and axe-core checks)
      // all content / interactive / widget roles can, but regular divs / text nodes cannot)
      if (triggerRef?.current && !__suppressAriaSupportCheck && isLiveRegion) {
        // TODO: change to console.warn in the next major
        // eslint-disable-next-line no-console -- warn is used to soft-deprecate outdated pattern
        console.info(
          "[ALCHEMY] Trying to show a tooltip on a non-supported HTML element or ARIA role",
          triggerRef?.current,
          `This is deprecated, use <Popper mode="popover"> activated by a button instead`
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- only need to run this once, and these dependencies should not be changed
    }, [isLiveRegion]);
    */
    // 1) Open on Focus if Tooltip and Keyboard
    useEffect(() => {
        if (mode !== "tooltip" || !triggerRef?.current) {
            return;
        }
        const trigger = triggerRef.current;
        const openOnFocusIfKeyboard = () => {
            // TODO: might be better to explicitly check for pointer instead to better support sighted SR users in browse mode
            if (whatInput.ask() === "keyboard") {
                openOnDelay();
            }
        };
        const closeOnBlur = (e) => {
            close(e);
        };
        trigger.addEventListener("focusin", openOnFocusIfKeyboard);
        trigger.addEventListener("focusout", closeOnBlur);
        return () => {
            trigger.removeEventListener("focusin", openOnFocusIfKeyboard);
            trigger.removeEventListener("focusout", closeOnBlur);
        };
    }, [close, mode, openOnDelay, triggerRef]);
    // Should be debounced and only be called once, even though attached to portalRoot AND window
    const closeOnOutsideInteraction = useDebounceSync(useCallback((e) => {
        if (!shouldCloseOnOutsideInteraction || !isOpen || !overlayRef.current) {
            return;
        }
        /* Check if isStillMounted because sometimes the contents of an overlay swap out and are removed before this callback runs (like in the VideoPlayer settings menu) and we can't check if the overlay contains an element no longer in the dom. In this case, just keep the menu open. This is naive but works well enough. */
        const isStillMounted = document.body.contains(e.target);
        /* Clicks on or inside of the trigger or overlay are not outside interactions. */
        const isTrigger = triggerRef?.current && triggerRef.current.contains(e.target);
        const isOverlay = overlayRef.current.contains(e.target);
        if (isTrigger || isOverlay || !isStillMounted) {
            return;
        }
        close(e);
    }, [close, isOpen, overlayRef, shouldCloseOnOutsideInteraction, triggerRef]), 16);
    // 2) Close On Escape (when in popover mode); close on outside interaction is handled by ClickAwayListener
    useEffect(() => {
        if (!isOpen || !overlayRef.current) {
            return;
        }
        const overlay = overlayRef.current;
        const closeMethod = mode === "popover" ? closeAndReturnFocus : close;
        const closeOnEsc = (e) => {
            if (e.key === "Escape") {
                closeMethod(e);
                e.stopPropagation();
            }
        };
        if (mode === "popover") {
            /* Add keydown event to overlay, which will have focus or focus within. This scopes the event to avoid conflicting with other escape listeners */
            overlay.addEventListener("keydown", closeOnEsc);
        }
        return () => {
            if (mode === "popover") {
                overlay.removeEventListener("keydown", closeOnEsc);
            }
        };
    }, [close, closeAndReturnFocus, isOpen, mode, overlayRef, portalRoot]);
    // 3) Trigger - Add a11y markup to
    // const hasWarnedAboutDescribedBy = useRef<boolean>(false); // DES-2482
    useEffect(() => {
        if (!triggerRef?.current) {
            return () => { };
        }
        // Based on https://www.w3.org/TR/wai-aria-1.2/#aria-expanded
        const ariaExpandableRoles = [
            "button",
            "checkbox",
            "columnheader",
            "combobox",
            "gridcell",
            "link",
            "listbox",
            "menuitem",
            "menuitemradio",
            "menuitemcheckbox",
            "radio",
            "row",
            "rowheader",
            "switch",
            "tab",
            "treeitem"
        ];
        const ariaExpandableElements = ["A", "BUTTON"];
        const isAriaExpandableRole = (role) => ariaExpandableRoles.includes(role);
        const isAriaExpandableElement = (e) => {
            if (e.hasAttribute("role")) {
                return isAriaExpandableRole(e.getAttribute("role"));
            }
            return ariaExpandableElements.includes(e.tagName);
        };
        const trigger = triggerRef.current;
        // TODO: update after DES-3288 to check explicitly for allowed roles instead of MenuListDetails option hack
        if (mode === "popover") {
            // TODO: set value depending on popover type (non-modal dialog or menu)
            trigger.setAttribute("aria-haspopup", "true");
            trigger.setAttribute("data-popover-expanded", String(isOpen));
            // check to see aria-expanded should be explicitly enabled on elements that support it (e.g. combobox)
            if (isAriaExpandableElement(trigger)) {
                // TODO: we need a way to jump to content through DOM in case of manual mode
                // (e.g. listbox in combobox not going to be accessible if not rendered immediately after input)
                trigger.setAttribute("aria-expanded", String(isOpen));
            }
            return () => {
                trigger.removeAttribute("aria-controls");
                trigger.removeAttribute("aria-haspopup");
                trigger.removeAttribute("aria-expanded");
            };
        }
        if (mode === "tooltip" && !isReactControlled) {
            /* DES-2482 TODO: warn about description still being rendered if custom aria-describedby is set
            if (
              isDescription &&
              trigger.getAttribute("aria-describedby") &&
              trigger.getAttribute("aria-describedby") !== overlayId &&
              !hasWarnedAboutDescribedBy.current
            ) {
              hasWarnedAboutDescribedBy.current = true;
              // TODO: change to console.warn in the next major
              // eslint-disable-next-line no-console -- warn is used to prevent an accessibility issue
              console.info(
                "[ALCHEMY] Tooltip describes an element which already has aria-describedby, attached via triggerRef, this is not supported",
                triggerRef?.current,
                `Tooltip contents are still going to be rendered next to the element, but aria-describedby is not overridden on the element. Use silent mode and add tooltip contents to element description explicitly instead.`
              );
            }
            */
            // support non-React-controlled elements via triggerRef
            if (isLabel && !trigger.getAttribute("aria-labelledby") && !trigger.getAttribute("aria-label")) {
                trigger.setAttribute("aria-labelledby", overlayId);
            }
            else if (isDescription && !trigger.getAttribute("aria-describedby")) {
                trigger.setAttribute("aria-describedby", overlayId);
            }
            return () => {
                if (trigger.getAttribute("aria-labelledby") === overlayId) {
                    trigger.removeAttribute("aria-labelledby");
                }
                if (trigger.getAttribute("aria-describedby") === overlayId) {
                    trigger.removeAttribute("aria-describedby");
                }
            };
        }
        // TODO: Add Ctrl/Escape closing to trigger? (DES-3477)
        return () => { };
    }, [isDescription, isLabel, isOpen, isReactControlled, mode, overlayId, triggerRef]);
    // 4) Overlay markup
    const overlayRole = popoverMode === "dialog"
        ? "dialog"
        : // TODO: This only exists for backward compatibility and should be removed, as support for role="tooltip" is [virtually non-existent](https://a11ysupport.io/tech/aria/tooltip_role)
            mode === "tooltip"
                ? "tooltip"
                : undefined;
    // 5) Focus popover when fully open
    const isOpenAndMounted = (isOpen && motionState === MotionState.Opened) || motionState === MotionState.Opening;
    const shouldFocusOverlay = mode === "popover" && popoverMode !== "manual" && isOpenAndMounted;
    useEffect(() => {
        const overlay = overlayRef.current;
        // TODO: Bug here if someone changes popoverMode or shouldFocusOverlay while popper is open
        // the first focusable element will re-focus
        if (!shouldFocusOverlay || !overlay || overlay.contains(document.activeElement)) {
            return;
        }
        // Skip raf if in test environment
        if (process.env.NODE_ENV === "test") {
            const elementToFocus = getElementToFocus(overlay);
            elementToFocus?.focus();
            return;
        }
        // wait until next frame when popper is in position
        // to prevent page jump
        const frame = window.requestAnimationFrame(() => {
            if (overlay.contains(document.activeElement)) {
                return;
            }
            const elementToFocus = getElementToFocus(overlay);
            elementToFocus?.focus();
        });
        return () => window.cancelAnimationFrame(frame);
    }, [shouldFocusOverlay, overlayRef, popoverMode]);
    return {
        closeOnOutsideInteraction,
        overlayId,
        overlayRole,
        // only ARIA description needs to be next to the trigger in order to be accessible;
        shouldPortalTooltipAriaRegion: mode === "tooltip" && !isDescription,
        tooltipAriaRegion: mode === "tooltip" && !isSilent ? (_jsx(ScreenReaderContent, { ref: overlayHiddenRef, id: overlayId, role: isLiveRegion ? "status" : "none", "aria-live": isLiveRegion ? "polite" : "off", "aria-hidden": isLabel, children: isLiveRegionAnnounced || isLabelRendered || isDescriptionRendered ? overlayReactElement : null })) : null,
        triggerProps
    };
};
